import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { notificationApi } from '../api/notificationApi'
import { useWebSocket } from '@/hooks/useWebSocket'
import type {
  NotificationListResponse,
  UnreadCountResponse,
  NotificationFilters,
  UserNotification,
} from '../types'

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (filters?: NotificationFilters) => [...notificationKeys.all, 'list', filters] as const,
  unreadCount: () => [...notificationKeys.all, 'unreadCount'] as const,
}

/**
 * Hook to list notifications with filters.
 *
 * `enabled` (default true) gates the query entirely — pass `enabled: isOpen`
 * from a closed-by-default surface like the bell dropdown so the full list
 * (up to `limit` rows) is fetched on demand when the panel opens, not on
 * every mount + every WebSocket `notification.created` invalidation while
 * it's closed and nobody can see it (LIVE-26 polling-reduction pass —
 * NotificationBell used to call this unconditionally, so a duplicate-alert
 * flood that fires many `notification.created` events in quick succession
 * multiplied into that many list refetches even with the panel closed).
 */
export function useNotifications(
  filters?: NotificationFilters,
  options?: { enabled?: boolean }
) {
  const queryClient = useQueryClient()
  const { addHandler, isConnected } = useWebSocket({ autoConnect: true })
  const enabled = options?.enabled ?? true

  // Invalidate queries when new notification arrives via WebSocket
  useEffect(() => {
    if (!isConnected) return

    const removeHandler = addHandler((message) => {
      if (message.type === 'notification.created') {
        // Invalidate both list and count queries
        queryClient.invalidateQueries({ queryKey: notificationKeys.all })
      }
    })

    return removeHandler
  }, [isConnected, addHandler, queryClient])

  return useQuery<NotificationListResponse>({
    queryKey: notificationKeys.list(filters),
    queryFn: () => notificationApi.list(filters),
    staleTime: 30 * 1000, // 30 seconds
    enabled,
  })
}

/**
 * Hook to get unread notification count.
 *
 * The badge itself must keep working while the bell is closed (that's the
 * point of a badge), so this query always stays `enabled` and still updates
 * in real time off the WebSocket `notification.created` invalidation above.
 * What LIVE-26 asked to cut is the blind 60s poll running forever whether or
 * not anyone has the dropdown open — `pollWhileOpen` (default false, i.e. no
 * timer) lets a caller opt into that fallback poll only while its panel is
 * actually visible.
 */
export function useUnreadCount(options?: { pollWhileOpen?: boolean }) {
  const queryClient = useQueryClient()
  const { addHandler, isConnected } = useWebSocket({ autoConnect: true })
  const pollWhileOpen = options?.pollWhileOpen ?? false

  // Update count when new notification arrives
  useEffect(() => {
    if (!isConnected) return

    const removeHandler = addHandler((message) => {
      if (message.type === 'notification.created') {
        queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
      }
    })

    return removeHandler
  }, [isConnected, addHandler, queryClient])

  return useQuery<UnreadCountResponse>({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationApi.getUnreadCount(),
    staleTime: 30 * 1000,
    refetchInterval: pollWhileOpen ? 60 * 1000 : false, // fallback poll only while visible
  })
}

/**
 * Hook to mark a notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => notificationApi.markAsRead(id),
    onMutate: async (id: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.all })

      // Snapshot the previous value
      const previousList = queryClient.getQueryData<NotificationListResponse>(notificationKeys.list())
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(notificationKeys.unreadCount())

      // Optimistically update the list
      if (previousList) {
        queryClient.setQueryData<NotificationListResponse>(notificationKeys.list(), {
          ...previousList,
          data: previousList.data.map((n) =>
            n.id === id ? { ...n, status: 'read' as const, read_at: new Date().toISOString() } : n
          ),
        })
      }

      // Optimistically update the count
      if (previousCount && previousCount.count > 0) {
        queryClient.setQueryData<UnreadCountResponse>(notificationKeys.unreadCount(), {
          ...previousCount,
          count: previousCount.count - 1,
        })
      }

      return { previousList, previousCount }
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousList) {
        queryClient.setQueryData(notificationKeys.list(), context.previousList)
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationKeys.unreadCount(), context.previousCount)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => notificationApi.markAllAsRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all })

      const previousList = queryClient.getQueryData<NotificationListResponse>(notificationKeys.list())
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(notificationKeys.unreadCount())

      // Optimistically update the list
      if (previousList) {
        queryClient.setQueryData<NotificationListResponse>(notificationKeys.list(), {
          ...previousList,
          data: previousList.data.map((n) => ({
            ...n,
            status: 'read' as const,
            read_at: n.read_at || new Date().toISOString(),
          })),
        })
      }

      // Optimistically update the count to 0
      queryClient.setQueryData<UnreadCountResponse>(notificationKeys.unreadCount(), {
        success: true,
        count: 0,
      })

      return { previousList, previousCount }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(notificationKeys.list(), context.previousList)
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationKeys.unreadCount(), context.previousCount)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Hook to delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => notificationApi.delete(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all })

      const previousList = queryClient.getQueryData<NotificationListResponse>(notificationKeys.list())
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(notificationKeys.unreadCount())

      // Optimistically remove from list
      if (previousList) {
        const deletedNotification = previousList.data.find((n) => n.id === id)
        queryClient.setQueryData<NotificationListResponse>(notificationKeys.list(), {
          ...previousList,
          data: previousList.data.filter((n) => n.id !== id),
          pagination: {
            ...previousList.pagination,
            total: previousList.pagination.total - 1,
          },
        })

        // Update count if the deleted notification was unread
        if (deletedNotification?.status === 'unread' && previousCount && previousCount.count > 0) {
          queryClient.setQueryData<UnreadCountResponse>(notificationKeys.unreadCount(), {
            ...previousCount,
            count: previousCount.count - 1,
          })
        }
      }

      return { previousList, previousCount }
    },
    onError: (_err, _id, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(notificationKeys.list(), context.previousList)
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationKeys.unreadCount(), context.previousCount)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Hook for notification events via WebSocket
 */
export function useNotificationEvents(
  onNotification?: (notification: UserNotification) => void
) {
  const { addHandler, isConnected } = useWebSocket({ autoConnect: true })

  useEffect(() => {
    if (!isConnected || !onNotification) return

    const removeHandler = addHandler((message) => {
      if (message.type === 'notification.created') {
        const notification = (message.data as { notification?: UserNotification })?.notification
        if (notification) {
          onNotification(notification)
        }
      }
    })

    return removeHandler
  }, [isConnected, addHandler, onNotification])
}
